import {
  Button,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow
} from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import React, { useState, useEffect, useCallback } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import AddVendorModal from './add-vendor-modal';
import VendorBulkActions from './bulk-vendor-actions';
import ArchiveModal from 'components/modals/archive-vendor-modal';
import { VendorType } from 'models/vendors.model';
import { apiService } from 'services/base.service';
import { debounce, formatDate, handleApiError } from 'utils/functions';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import useVendorContext from 'contexts/vendor.context';

const tableHeaders = [
  'Vendor name',
  'Phone number',
  'Date added',
  'Total paid',
  'Outstanding',
  '',
  ''
];

const Vendors = () => {
  const navigate = useNavigate();
  const [selectedVendors, setSelectedVendors] = useState<VendorType[]>([]);
  const [vendorsArray, setVendorsArray] = useState<VendorType[]>([]);
  const [ShowAddvendorModal, setShowAddvendorModal] = useState(false);
  const [isFetchingVendors, setIsFetchingVendors] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const { setVendorToEdit, setActiveVendorProfile } = useVendorContext();
  const [archiveVendorIds, setArchiveVendorIds] = useState<string[]>([]);
  const [archiveVendorName, setArchiveVendorName] = useState<string>('');

  const isAllSelected = () => {
    return vendorsArray.length > 0 && vendorsArray.length === selectedVendors.length;
  };

  const selectVendor = (id: string) => {
    const vendor = vendorsArray.find((v) => v.id === id);
    if (vendor) {
      setSelectedVendors((prev) =>
        prev.includes(vendor) ? prev.filter((v) => v.id !== id) : [...prev, vendor]
      );
    }
  };

  const handleArchive = (vendor?: VendorType) => {
    if (vendor) {
      setArchiveVendorIds([vendor.id]);
      setArchiveVendorName(vendor.name);
    } else {
      setArchiveVendorIds(selectedVendors.map((v) => v.id));
      setArchiveVendorName('');
    }
  };

  const toggleAll = () => {
    setSelectedVendors(isAllSelected() ? [] : vendorsArray);
  };

  const editVendor = (vendor: VendorType) => {
    setVendorToEdit(vendor);
    navigate(MAIN_STORE_ROUTE_KEYS.EDIT_VENDOR_DETAILS);
  };

  const viewProfile = (vendor: VendorType) => {
    setActiveVendorProfile(vendor);
    navigate(`${vendor.id}`);
  };

  const fetchVendors = async (searchString: string, current_page?: number) => {
    try {
      setIsFetchingVendors(true);
      const response = await apiService.get('vendors', {
        version: 'v2',
        params: {
          name: searchString,
          page: current_page || 1,
          size: 15
        }
      });
      setVendorsArray(response.data.data || []);
      setLastPage(Math.ceil(response.data.pagination.total / response.data.pagination.page_size));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingVendors(false);
    }
  };

  const debounceFetchVendors = useCallback(debounce(fetchVendors, 1000), []);

  useEffect(() => {
    setIsFetchingVendors(true);
    setVendorsArray([]);
    setSelectedVendors([]);
    debounceFetchVendors(search, currentPage);
  }, [search, currentPage]);

  const handleArchiveSuccess = () => {
    setSelectedVendors([]);
    setArchiveVendorIds([]);
    fetchVendors(search, currentPage);
  };

  return (
    <div className='px-2 md:px-6 mt-2 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Vendors</h4>
          <p className='text-[#6F7376] text-sm'>Manage and track vendors payment</p>
        </div>
        <Button
          onClick={() => setShowAddvendorModal(true)}
          variant='block'
          color='primary'
          className='w-[167px] text-white'
          small>
          <AddIcon className='w-[18px] h-[18px] text-white' />
          New Vendor
        </Button>
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Input
            type='search'
            placeholder='Search vendors'
            className='w-[300px]'
            inputClass='h-[44px]'
            value={search}
            onChange={(e) => {
              setCurrentPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => setCurrentPage(e.selected + 1)}
        />
      </div>
      <Table
        headers={tableHeaders}
        canSelect
        isAllSelected={isAllSelected()}
        onToggleAll={toggleAll}
        loading={isFetchingVendors}>
        {vendorsArray.map((vendor) => (
          <TableRow
            key={vendor.id}
            canSelect
            isSelected={selectedVendors.includes(vendor)}
            className='hover:bg-[#F5F6F6] table_row'
            onToggle={() => selectVendor(vendor.id)}>
            <TableData className='cursor-pointer hover:underline'>
              <span onClick={() => viewProfile(vendor)}>{vendor.name}</span>
            </TableData>
            <TableData>{vendor.phone_number}</TableData>
            <TableData> {formatDate(vendor.created_at)}</TableData>
            <TableData>{vendor.total_paid}</TableData>
            <TableData>{vendor.outstanding}</TableData>
            <TableData>
              <button className='underline text-[#E60A2B]' onClick={() => viewProfile(vendor)}>
                View Profile
              </button>
            </TableData>
            <TableData>
              <div className='flex gap-3 w-[52px]'>
                <EditIcon onClick={() => editVendor(vendor)} className='w-[18px] h-[18px] icon' />
                <ArchiveIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(vendor);
                  }}
                  className='w-[18px] h-[18px] icon cursor-pointer'
                />
              </div>
            </TableData>
          </TableRow>
        ))}
      </Table>
      {ShowAddvendorModal && (
        <AddVendorModal show={ShowAddvendorModal} onClose={() => setShowAddvendorModal(false)} />
      )}
      {archiveVendorIds.length > 0 && (
        <ArchiveModal
          show
          onClose={() => setArchiveVendorIds([])}
          vendorIds={archiveVendorIds}
          vendorName={archiveVendorName}
          onSuccess={handleArchiveSuccess}
        />
      )}
      {selectedVendors.length > 1 && (
        <VendorBulkActions
          item={selectedVendors}
          deleteFunc={() => handleArchive()}
          showBulkActions={true}
          setShowBulkActions={() => setSelectedVendors([])}
          setSelectedItems={setSelectedVendors}
        />
      )}
    </div>
  );
};

export default Vendors;
